/**
 * AdoraCare -- TEST-CLINIC JOURNEY page (ADORACARE-VISIT-CASE-SPINE-V1).
 * /dashboard/test-journey
 *
 * ONE clear five-step flow over EXISTING writers -- the founder's parallel
 * test operating spine, run end to end without EhNote:
 *
 *   1 Lead      GET /api/journey/leads/:leadId (the journey context read)
 *   2 Patient   the EXISTING patient-master search + POST
 *               /api/journey/leads/:id/patient-link (THE ONE conversion
 *               writer, trigger 'journey_identity_link')
 *   3 Visit     the EXISTING booked create + S2-P3 check-in hops
 *               (arrived / checked-in / fulfilled) -- the PLAN lane; this
 *               page never owns an appointment-status write path
 *   4 Diagnosis & treatment
 *               the EXISTING case-sheet endpoints (doctor-only writes; every
 *               other role renders its own honest role-safe shape from the
 *               compliance wall) + the EXISTING finalizeVisit orchestration
 *   5 Payment   the EXISTING patient-payments writer (#198) -- minor units
 *               by string math, idempotent requestId
 *
 * SENSITIVITY: diagnosis/treatment detail renders ONLY from the role-safe
 * caseSheet view the wire carries -- an owner sees counts, never narrative
 * or categories. Nothing on this page invents data; every state line is a
 * real wire field. NO ROI / ROAS / CAC copy anywhere.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { clinicLabelOf } from '@/dashboard/clinic-context';
import { Link, useSearchParams } from 'react-router-dom';
import { Route as RouteIcon } from 'lucide-react';

import {
  bookAppointment,
  createCaseSheet,
  createPlanItem,
  fetchJourneyLeadContext,
  markAppointmentArrived,
  markAppointmentCheckedIn,
  markAppointmentFulfilled,
  postJourneyPatientLinkApi,
  recordPatientPaymentApi,
  updateCaseSheet,
  markTreatmentAdvised,
  markTreatmentAccepted,
  markTreatmentDeclined,
  advanceLeadStageBackHalf,
  type CaseSheetClinicalView,
  type JourneyLeadContext,
  type PatientConvertCandidateView,
} from '@/dashboard/api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import type { DashboardMe } from '@/dashboard/types';
import {
  finalizeVisit,
  type FinalizeVisitDeps,
} from '@/dashboard/clinical-workbench/case-sheet-cockpit-view';
import {
  formatMinorUnitsInr,
  parseInrToMinorUnits,
  PAYMENT_METHOD_OPTIONS,
} from '@/dashboard/billing/billing-entry-view';
import { formatIstTime } from '@/lib/datetime-ist';
import {
  DaySlotPicker,
  useDaySlots,
} from '@/dashboard/appointments/DaySlotPicker';
import {
  appointmentStatusLabel,
  canLinkPatient,
  canOperateAppointment,
  canRecordPayment,
  canWriteCaseSheet,
  currentAppointment,
  deriveJourneySteps,
  isCaseSheetFinalized,
  isJourneyComplete,
  nextVisitAction,
  visitChainRank,
  VISIT_CHAIN,
  type JourneyStepState,
} from '@/dashboard/journey/journey-view';

// The finalize deps mirror the CaseSheetCockpit wiring literal exactly: the
// SAME pure finalizeVisit orchestration drives the SAME client fns, so there
// is exactly ONE finalize -> ONE recordOutcome path in the product.
const FINALIZE_DEPS: FinalizeVisitDeps = {
  updateCaseSheet,
  outcome: {
    markTreatmentAdvised,
    markTreatmentAccepted,
    markTreatmentDeclined,
    advanceLeadStageBackHalf,
  },
};

/** The frozen 8 controlled decline-reason tokens (OUTCOME_REASONS mirror). */
const DECLINE_REASON_OPTIONS: readonly string[] = [
  'price',
  'location',
  'timing',
  'no_response',
  'chose_competitor',
  'not_interested',
  'duplicate',
  'other',
];

type Feedback = { kind: 'success' | 'error'; text: string } | null;

function FeedbackLine({ feedback }: { feedback: Feedback }): JSX.Element | null {
  if (feedback === null) return null;
  return (
    <p
      className={`journey-feedback journey-feedback--${feedback.kind}`}
      role={feedback.kind === 'error' ? 'alert' : 'status'}
    >
      {feedback.text}
    </p>
  );
}

// --- the step rail -----------------------------------------------------------

export function StepRail({ steps }: { steps: readonly JourneyStepState[] }): JSX.Element {
  return (
    <ol className="journey__rail" data-testid="journey-rail">
      {steps.map((step, i) => (
        <li key={step.key} className="journey__rail-step">
          {i > 0 ? (
            <span
              className={
                steps[i - 1]!.status === 'done'
                  ? 'journey__rail-connector journey__rail-connector--done'
                  : 'journey__rail-connector'
              }
              aria-hidden="true"
            />
          ) : null}
          <span
            className={
              step.status === 'done'
                ? 'journey-dot journey-dot--done'
                : step.status === 'current'
                  ? 'journey-dot journey-dot--current'
                  : 'journey-dot'
            }
            aria-hidden="true"
          >
            {step.status === 'done' ? '\u2713' : step.index}
          </span>
          <span className="journey__rail-label">
            <span
              className={
                step.status === 'upcoming'
                  ? 'journey__rail-title journey__rail-title--upcoming'
                  : 'journey__rail-title'
              }
            >
              {step.title}
            </span>
            <span className="journey__rail-state">
              {step.status === 'done'
                ? 'Done'
                : step.status === 'current'
                  ? 'Current step'
                  : 'Upcoming'}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}

export function StepCard({
  step,
  children,
}: {
  step: JourneyStepState;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section
      className={
        step.status === 'current'
          ? 'journey-card journey-card--current'
          : step.status === 'done'
            ? 'journey-card journey-card--done'
            : 'journey-card'
      }
      data-testid={`journey-step-${step.key}`}
      aria-current={step.status === 'current' ? 'step' : undefined}
    >
      <div className="journey-card__head">
        <h3 className="journey-card__title">
          <span
            className={
              step.status === 'done'
                ? 'journey-dot journey-dot--done'
                : step.status === 'current'
                  ? 'journey-dot journey-dot--current'
                  : 'journey-dot'
            }
            aria-hidden="true"
          >
            {step.status === 'done' ? '\u2713' : step.index}
          </span>
          {step.title}
        </h3>
        <span
          className={
            step.status === 'done'
              ? 'badge badge--success'
              : step.status === 'current'
                ? 'badge badge--info'
                : 'badge'
          }
        >
          {step.status === 'done'
            ? 'Done'
            : step.status === 'current'
              ? 'Current step'
              : 'Upcoming'}
        </span>
      </div>
      <p className="journey-card__summary">{step.summary}</p>
      {children}
    </section>
  );
}

// --- Step 1: Lead --------------------------------------------------------------

export function LeadStepCard({
  step,
  ctx,
}: {
  step: JourneyStepState;
  ctx: JourneyLeadContext | null;
}): JSX.Element {
  return (
    <StepCard step={step}>
      {ctx === null ? (
        <p className="journey-note">
          Enter a lead ID above, or open a lead from the{' '}
          <Link to="/dashboard/lead-inbox">Lead inbox</Link>.
        </p>
      ) : (
        <>
          <dl className="journey-facts">
            <div>
              <dt>Lead</dt>
              <dd>{ctx.lead.name ?? 'No name captured'}</dd>
            </div>
            <div>
              <dt>Source</dt>
              <dd>{ctx.lead.sourceNormalized ?? 'Unknown'}</dd>
            </div>
            <div>
              <dt>Clinic</dt>
              <dd>
                {ctx.lead.clinicId === null
                  ? 'Pending assignment'
                  : clinicLabelOf(ctx.lead.clinicLabel)}
              </dd>
            </div>
            <div>
              <dt>Stage</dt>
              <dd>{ctx.lead.stage ?? 'Not recorded'}</dd>
            </div>
            <div>
              <dt>Captured</dt>
              <dd>{new Date(ctx.lead.createdAt).toLocaleDateString('en-IN')}</dd>
            </div>
          </dl>
          {ctx.lead.clinicId === null ? (
            <p className="journey-note">
              This lead has no clinic yet. Assign one in{' '}
              <Link to="/dashboard/org-intake">Org intake</Link> before starting
              the visit.
            </p>
          ) : null}
        </>
      )}
    </StepCard>
  );
}

// --- Step 2: Patient ------------------------------------------------------------

/**
 * JOURNEY-ATTACH-CANDIDACY-FENCE-V1 (Refs #315). The org-wide patient search
 * that used to feed this step is RETIRED: picking a row out of an org-wide
 * search proved the patient existed in the tenant, never that this lead was
 * that person (founder ruling 2026-07-31 -- "An org-wide search selection
 * alone is not identity proof"). The step now runs the convert door's flow:
 * ASK who this lead's number could belong to (a derived, zero-write server
 * answer), then pick a person from THAT set. The server re-resolves the set
 * on the link call and refuses anything it did not surface, so this component
 * is convenience; the fence is api/routes/journey.ts.
 */
export function PatientStepCard({
  step,
  ctx,
  me,
  onLinked,
}: {
  step: JourneyStepState;
  ctx: JourneyLeadContext | null;
  me: DashboardMe;
  onLinked: () => void;
}): JSX.Element {
  const [candidates, setCandidates] = useState<
    readonly PatientConvertCandidateView[] | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const linked = ctx !== null && ctx.conversion.patientId !== null;
  const allowed = canLinkPatient(me.role);
  const patientMasterOn = me.patientMasterEnabled === true;

  /** Ask the server who this lead's phone could be. ZERO writes: the door
   *  answers with the derived confirmation state and its candidates. */
  const onShowCandidates = async (): Promise<void> => {
    if (ctx === null) return;
    setBusy(true);
    setFeedback(null);
    setCandidates(null);
    try {
      const res = await postJourneyPatientLinkApi(ctx.lead.leadId);
      setCandidates(res.candidates ?? []);
    } catch {
      setFeedback({
        kind: 'error',
        text: 'Could not check who this number belongs to. Try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  const onLink = async (patientId: string): Promise<void> => {
    if (ctx === null) return;
    setBusy(true);
    setFeedback(null);
    try {
      const res = await postJourneyPatientLinkApi(ctx.lead.leadId, {
        action: 'link',
        patientId,
      });
      if (res.patientIdStamped || res.reason === 'already_converted') {
        setFeedback({ kind: 'success', text: 'Lead linked to the patient record.' });
        onLinked();
      } else {
        setFeedback({
          kind: 'success',
          text: 'Link recorded (conversion milestone updated).',
        });
        onLinked();
      }
    } catch (err) {
      const code =
        err !== null && typeof err === 'object' && 'code' in err
          ? (err as { code?: unknown }).code
          : null;
      setFeedback({
        kind: 'error',
        text:
          code === 'lead_patient_link_conflict'
            ? 'This lead is already linked to a different patient.'
            : code === 'patient_not_a_candidate'
              ? 'That patient does not match this lead. Pick one of the people shown.'
              : 'Could not link the patient. Check the selection and try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <StepCard step={step}>
      {ctx === null ? null : linked ? (
        <dl className="journey-facts">
          <div>
            <dt>Patient</dt>
            <dd>{ctx.patient?.fullName ?? 'Linked patient'}</dd>
          </div>
          <div>
            <dt>UMR</dt>
            <dd>{ctx.patient?.umr ?? 'Not available'}</dd>
          </div>
          <div>
            <dt>Contact milestone</dt>
            <dd>
              {/* V-1 (founder-review corrective PR #271): this milestone is the
                  lead->patient IDENTITY link (contacts.converted_to_patient),
                  NOT the payment-derived commercial state. It renders EXPLICIT
                  identity-link copy so the reserved bare "Converted" can never
                  be mistaken for commercial conversion here. */}
              {ctx.conversion.contactConverted ? (
                <span className="badge badge--success">Linked to patient record</span>
              ) : (
                <span className="badge">Not linked yet</span>
              )}
            </dd>
          </div>
        </dl>
      ) : !patientMasterOn ? (
        <p className="journey-note">
          Patient Master is not enabled for your account, so patients cannot be
          searched or created here yet.
        </p>
      ) : !allowed ? (
        <p className="journey-note">
          The front desk links the lead to a patient record (receptionist, COC,
          or owner).
        </p>
      ) : (
        <>
          <div className="journey-form">
            <div className="journey-field">
              <span className="journey-field__hint">
                A phone match shows who this number could be; it never links
                anyone on its own. Pick the person who enquired. New person?{' '}
                <Link to="/dashboard/patients/new">Create the patient</Link>{' '}
                first.
              </span>
            </div>
            <div className="journey-actions">
              <button
                type="button"
                className="ac-btn ac-btn--primary"
                onClick={() => void onShowCandidates()}
                disabled={busy}
              >
                {busy ? 'Checking...' : 'Check who this number belongs to'}
              </button>
            </div>
          </div>
          {candidates !== null ? (
            candidates.length === 0 ? (
              <p className="journey-note">
                No patient on record shares this lead's number. Create one via{' '}
                <Link to="/dashboard/patients/new">New patient</Link>, then
                check again.
              </p>
            ) : (
              <ul className="journey-plan">
                {candidates.map((c) => (
                  <li key={c.patientId} className="journey-plan__row">
                    <span>
                      <strong>{c.fullName}</strong>{' '}
                      <span className="cockpit-pill cockpit-pill--muted">{c.umr}</span>
                      {typeof c.ageYears === 'number' ? ` - ${c.ageYears} yrs` : ''}
                      {c.gender ? ` - ${c.gender}` : ''}
                      {c.city ? ` - ${c.city}` : ''}
                    </span>
                    <button
                      type="button"
                      className="ac-btn ac-btn--primary ac-btn--sm"
                      onClick={() => void onLink(c.patientId)}
                      disabled={busy}
                    >
                      Link to this lead
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : null}
          <FeedbackLine feedback={feedback} />
        </>
      )}
    </StepCard>
  );
}

// --- Step 3: Visit (the PLAN lane; composes the EXISTING writers) ---------------

export function VisitStepCard({
  step,
  ctx,
  me,
  onChanged,
}: {
  step: JourneyStepState;
  ctx: JourneyLeadContext | null;
  me: DashboardMe;
  onChanged: () => void;
}): JSX.Element {
  // LEAD-APPOINTMENT-CRM-COHERENCE-V1 (SCOPE 2): the shared server-computed slot
  // grid (DaySlotPicker) - the same picker the cockpit and the Lead-360 workbench
  // use. The chosen slot is already a UTC instant, so no wall-clock conversion.
  const [slotDate, setSlotDate] = useState('');
  const [selectedIso, setSelectedIso] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [timeError, setTimeError] = useState<string | null>(null);

  const appt = ctx === null ? null : currentAppointment(ctx);
  const allowed = canOperateAppointment(me.role);
  const bookingOn = me.crmAppointmentWorkbenchEnabled === true;
  const slots = useDaySlots(ctx === null ? null : ctx.lead.leadId, slotDate);

  const onBook = async (): Promise<void> => {
    if (ctx === null || ctx.lead.clinicId === null) return;
    if (selectedIso === '') {
      setTimeError('Pick a slot from the grid.');
      return;
    }
    setTimeError(null);
    setBusy(true);
    setFeedback(null);
    try {
      await bookAppointment(ctx.lead.leadId, selectedIso);
      setFeedback({ kind: 'success', text: 'Appointment booked.' });
      onChanged();
    } catch (err) {
      // ERROR-PASSTHROUGH LAW: render the server's plain-language message
      // VERBATIM for the booking-domain 400/409 failures (e.g. "This slot is
      // already booked...", "Outside clinic hours..."); never a hardcoded
      // reconstruction. Any other status - incl. a 500 whose DatabaseError body
      // is a RAW driver string - uses the safe generic fallback (LAW 3a).
      const status =
        err !== null && typeof err === 'object' && 'status' in err
          ? (err as { status?: unknown }).status
          : null;
      const text =
        (status === 400 || status === 409) &&
        err instanceof Error &&
        err.message.trim() !== '' &&
        !/^HTTP \d+/.test(err.message)
          ? err.message
          : 'Could not book the appointment. Please try again.';
      setFeedback({ kind: 'error', text });
    } finally {
      setBusy(false);
    }
  };

  const onAdvance = async (): Promise<void> => {
    if (appt === null) return;
    const action = nextVisitAction(appt.status);
    if (action === null) return;
    setBusy(true);
    setFeedback(null);
    try {
      if (action === 'arrived') await markAppointmentArrived(appt.appointmentId);
      else if (action === 'checked_in')
        await markAppointmentCheckedIn(appt.appointmentId);
      else await markAppointmentFulfilled(appt.appointmentId);
      setFeedback({ kind: 'success', text: 'Appointment status updated.' });
      onChanged();
    } catch {
      setFeedback({
        kind: 'error',
        text: 'Status update failed. The appointment may have changed - reload.',
      });
    } finally {
      setBusy(false);
    }
  };

  const nextAction = appt === null ? null : nextVisitAction(appt.status);
  const nextActionLabel =
    nextAction === 'arrived'
      ? 'Mark arrived'
      : nextAction === 'checked_in'
        ? 'Mark checked in'
        : nextAction === 'fulfilled'
          ? 'Mark fulfilled'
          : null;

  return (
    <StepCard step={step}>
      {ctx === null ? null : appt === null ? (
        !bookingOn ? (
          <p className="journey-note">
            Appointment booking is not enabled for your account yet, so the
            visit cannot be planned from here.
          </p>
        ) : !allowed ? (
          <p className="journey-note">
            The front desk books the appointment (receptionist, COC, or owner).
          </p>
        ) : ctx.lead.clinicId === null ? (
          <p className="journey-note">Assign a clinic first (step 1).</p>
        ) : (
          <div className="journey-form">
            <DaySlotPicker
              date={slotDate}
              onDateChange={(d) => {
                setSlotDate(d);
                setSelectedIso('');
              }}
              slots={slots}
              selectedIso={selectedIso}
              onSelectIso={setSelectedIso}
              disabled={busy}
              idPrefix="journey-appt"
            />
            {timeError ? (
              <span className="journey-field__error">{timeError}</span>
            ) : (
              <span className="journey-field__hint">
                Booked at the lead&apos;s clinic
                {ctx.lead.clinicLabel ? ` (${ctx.lead.clinicLabel})` : ''}.
              </span>
            )}
            <div className="journey-actions">
              <button
                type="button"
                className="ac-btn ac-btn--primary"
                onClick={() => void onBook()}
                disabled={busy || selectedIso === ''}
              >
                {busy ? 'Booking...' : 'Book appointment'}
              </button>
            </div>
          </div>
        )
      ) : (
        <>
          <dl className="journey-facts">
            <div>
              <dt>Appointment</dt>
              <dd>#{appt.appointmentId}</dd>
            </div>
            <div>
              <dt>Time (IST)</dt>
              <dd>
                {new Date(appt.appointmentTime).toLocaleDateString('en-IN')}{' '}
                {formatIstTime(appt.appointmentTime)}
              </dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{appointmentStatusLabel(appt.status)}</dd>
            </div>
          </dl>
          <div className="journey-chain" aria-label="Visit progress">
            {VISIT_CHAIN.map((c, i) => (
              <span
                key={c.status}
                className={
                  visitChainRank(appt.status) >= i ? 'badge badge--success' : 'badge'
                }
              >
                {c.label}
              </span>
            ))}
          </div>
          {nextActionLabel !== null ? (
            allowed ? (
              <div className="journey-actions">
                <button
                  type="button"
                  className="ac-btn ac-btn--primary"
                  onClick={() => void onAdvance()}
                  disabled={busy}
                >
                  {busy ? 'Updating...' : nextActionLabel}
                </button>
              </div>
            ) : (
              <p className="journey-note">
                The front desk records arrival and check-in (receptionist, COC,
                or owner).
              </p>
            )
          ) : null}
          <FeedbackLine feedback={feedback} />
        </>
      )}
    </StepCard>
  );
}

// --- Step 4: Diagnosis & treatment (the VISIT record; doctor-only writes) -------

export function TreatmentStepCard({
  step,
  ctx,
  me,
  onChanged,
}: {
  step: JourneyStepState;
  ctx: JourneyLeadContext | null;
  me: DashboardMe;
  onChanged: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [complaintCategory, setComplaintCategory] = useState('');
  const [diagnosisCategory, setDiagnosisCategory] = useState('');
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [findings, setFindings] = useState('');
  const [procName, setProcName] = useState('');
  const [procValue, setProcValue] = useState('');
  const [procError, setProcError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState('');
  const [declineReason, setDeclineReason] = useState('');

  const appt = ctx === null ? null : currentAppointment(ctx);
  const sheet = ctx === null ? null : ctx.caseSheet;
  const isDoctor = canWriteCaseSheet(me.role);
  const clinical =
    sheet !== null && sheet.audience === 'clinical'
      ? (sheet as CaseSheetClinicalView)
      : null;

  // Seed the doctor's edit fields from the loaded sheet.
  useEffect(() => {
    if (clinical !== null) {
      setComplaintCategory(clinical.chiefComplaintCategory ?? '');
      setDiagnosisCategory(clinical.diagnosisCategory ?? '');
      setChiefComplaint(clinical.chiefComplaint ?? '');
      setFindings(clinical.findingsDiagnosis ?? '');
    }
  }, [clinical?.id, clinical?.updatedAt]);

  const onStartOrSave = async (): Promise<void> => {
    if (appt === null) return;
    setBusy(true);
    setFeedback(null);
    try {
      const fields = {
        chiefComplaint: chiefComplaint.trim() === '' ? null : chiefComplaint.trim(),
        findingsDiagnosis: findings.trim() === '' ? null : findings.trim(),
        chiefComplaintCategory: complaintCategory === '' ? null : complaintCategory,
        diagnosisCategory: diagnosisCategory === '' ? null : diagnosisCategory,
      };
      if (clinical === null) {
        await createCaseSheet({ appointmentId: appt.appointmentId, ...fields });
        setFeedback({ kind: 'success', text: 'Visit record started.' });
      } else {
        await updateCaseSheet(clinical.id, fields);
        setFeedback({ kind: 'success', text: 'Visit record saved.' });
      }
      onChanged();
    } catch {
      setFeedback({
        kind: 'error',
        text: 'Could not save the visit record. Check the fields and try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  const onAddItem = async (): Promise<void> => {
    if (clinical === null) return;
    if (procName.trim() === '') {
      setProcError('Procedure name is required.');
      return;
    }
    const value =
      procValue.trim() === '' ? null : Number.parseInt(procValue.trim(), 10);
    if (value !== null && (!Number.isSafeInteger(value) || value < 0)) {
      setProcError('Estimated value must be a whole rupee amount.');
      return;
    }
    setProcError(null);
    setBusy(true);
    setFeedback(null);
    try {
      await createPlanItem(clinical.id, {
        procedureName: procName.trim(),
        estimatedValue: value,
        status: 'advised',
      });
      setProcName('');
      setProcValue('');
      setFeedback({ kind: 'success', text: 'Plan item added.' });
      onChanged();
    } catch {
      setFeedback({ kind: 'error', text: 'Could not add the plan item.' });
    } finally {
      setBusy(false);
    }
  };

  const onFinalize = async (): Promise<void> => {
    if (clinical === null || appt === null) return;
    setBusy(true);
    setFeedback(null);
    const visitOutcome =
      outcome === ''
        ? null
        : {
            action: outcome as
              | 'treatment_advised'
              | 'treatment_accepted'
              | 'treatment_declined',
            declineReason: outcome === 'treatment_declined' ? declineReason : '',
          };
    const result = await finalizeVisit(FINALIZE_DEPS, {
      caseSheetId: clinical.id,
      appointmentId: appt.appointmentId,
      visitOutcome,
    });
    setFeedback({
      kind: result.feedback.kind === 'error' ? 'error' : 'success',
      text: result.feedback.lines.join(' '),
    });
    setBusy(false);
    onChanged();
  };

  const categoryOptions = ctx === null ? [] : ctx.categoryOptions;

  return (
    <StepCard step={step}>
      {ctx === null ? null : appt === null ? (
        <p className="journey-note">Plan the visit first (step 3).</p>
      ) : sheet === null && !isDoctor ? (
        <p className="journey-note">
          The doctor records the visit (diagnosis and treatment plan) once the
          patient arrives. This step is read-only for your role.
        </p>
      ) : sheet !== null && !isDoctor ? (
        // Role-safe render: exactly what the compliance wall returned.
        <dl className="journey-facts">
          <div>
            <dt>Visit record</dt>
            <dd>{isCaseSheetFinalized(sheet) ? 'Finalized' : 'Draft'}</dd>
          </div>
          {sheet.audience === 'analytics' ? (
            <>
              <div>
                <dt>Plan items</dt>
                <dd>{sheet.itemCount}</dd>
              </div>
              <div>
                <dt>Accepted</dt>
                <dd>{sheet.acceptedCount}</dd>
              </div>
              <div>
                <dt>Declined</dt>
                <dd>{sheet.declinedCount}</dd>
              </div>
              <div>
                <dt>Plan total</dt>
                <dd>{formatMinorUnitsInr(sheet.totalEstimatedValue * 100)}</dd>
              </div>
            </>
          ) : sheet.audience === 'operational' ? (
            <>
              <div>
                <dt>Reason category</dt>
                <dd>{sheet.chiefComplaintCategory ?? 'Not recorded'}</dd>
              </div>
              <div>
                <dt>Diagnosis category</dt>
                <dd>{sheet.diagnosisCategory ?? 'Not recorded'}</dd>
              </div>
              <div>
                <dt>Plan items</dt>
                <dd>{sheet.planItems.length}</dd>
              </div>
              <div>
                <dt>Declined</dt>
                <dd>{sheet.declinedItemCount}</dd>
              </div>
            </>
          ) : sheet.audience === 'billing' ? (
            <>
              <div>
                <dt>Plan items</dt>
                <dd>{sheet.planItems.length}</dd>
              </div>
              <div>
                <dt>Plan total</dt>
                <dd>{formatMinorUnitsInr(sheet.totalEstimatedValue * 100)}</dd>
              </div>
            </>
          ) : null}
        </dl>
      ) : isDoctor && clinical !== null && isCaseSheetFinalized(clinical) ? (
        <>
          <dl className="journey-facts">
            <div>
              <dt>Visit record</dt>
              <dd>Finalized (locked)</dd>
            </div>
            <div>
              <dt>Reason category</dt>
              <dd>{clinical.chiefComplaintCategory ?? 'Not recorded'}</dd>
            </div>
            <div>
              <dt>Diagnosis category</dt>
              <dd>{clinical.diagnosisCategory ?? 'Not recorded'}</dd>
            </div>
            <div>
              <dt>Plan items</dt>
              <dd>{clinical.planItems.length}</dd>
            </div>
          </dl>
        </>
      ) : (
        <>
          <div className="journey-form">
            <div className="journey-field">
              <label className="journey-field__label" htmlFor="journey-cc-cat">
                Reason category
              </label>
              <select
                id="journey-cc-cat"
                value={complaintCategory}
                onChange={(e) => setComplaintCategory(e.target.value)}
                disabled={busy || categoryOptions.length === 0}
              >
                <option value="">Not recorded</option>
                {categoryOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              {categoryOptions.length === 0 ? (
                <span className="journey-field__hint">
                  No category vocabulary configured for this clinic yet.
                </span>
              ) : null}
            </div>
            <div className="journey-field">
              <label className="journey-field__label" htmlFor="journey-dx-cat">
                Diagnosis category
              </label>
              <select
                id="journey-dx-cat"
                value={diagnosisCategory}
                onChange={(e) => setDiagnosisCategory(e.target.value)}
                disabled={busy || categoryOptions.length === 0}
              >
                <option value="">Not recorded</option>
                {categoryOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="journey-field journey-field--wide">
              <label className="journey-field__label" htmlFor="journey-cc">
                Chief complaint (doctor-only note)
              </label>
              <textarea
                id="journey-cc"
                rows={2}
                value={chiefComplaint}
                onChange={(e) => setChiefComplaint(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="journey-field journey-field--wide">
              <label className="journey-field__label" htmlFor="journey-dx">
                Findings / diagnosis (doctor-only note)
              </label>
              <textarea
                id="journey-dx"
                rows={2}
                value={findings}
                onChange={(e) => setFindings(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>
          <div className="journey-actions">
            {/* ONE primary action per state (the UI-bar amendment): starting
                the record is the primary until it exists; then Finalize (below)
                is the step's primary and saving becomes secondary. */}
            <button
              type="button"
              className={
                clinical === null ? 'ac-btn ac-btn--primary' : 'ac-btn ac-btn--secondary'
              }
              onClick={() => void onStartOrSave()}
              disabled={busy}
            >
              {busy
                ? 'Saving...'
                : clinical === null
                  ? 'Start visit record'
                  : 'Save visit record'}
            </button>
          </div>
          {clinical !== null ? (
            <>
              <ul className="journey-plan">
                {clinical.planItems.map((item) => (
                  <li key={item.id} className="journey-plan__row">
                    <span>
                      <strong>{item.procedureName}</strong>
                      {item.estimatedValue !== null
                        ? ` - ${formatMinorUnitsInr(item.estimatedValue * 100)}`
                        : ''}
                    </span>
                    <span className="badge">{item.status}</span>
                  </li>
                ))}
              </ul>
              <div className="journey-form">
                <div className="journey-field">
                  <label className="journey-field__label" htmlFor="journey-proc">
                    Procedure
                  </label>
                  <input
                    id="journey-proc"
                    className="journey-field__input"
                    value={procName}
                    onChange={(e) => setProcName(e.target.value)}
                    placeholder="e.g. Root canal treatment"
                    disabled={busy}
                  />
                  {procError ? (
                    <span className="journey-field__error">{procError}</span>
                  ) : null}
                </div>
                <div className="journey-field">
                  <label className="journey-field__label" htmlFor="journey-proc-value">
                    Estimated value (whole INR)
                  </label>
                  <input
                    id="journey-proc-value"
                    className="journey-field__input"
                    inputMode="numeric"
                    value={procValue}
                    onChange={(e) => setProcValue(e.target.value)}
                    placeholder="e.g. 8000"
                    disabled={busy}
                  />
                </div>
                <div className="journey-actions">
                  <button
                    type="button"
                    className="ac-btn ac-btn--secondary"
                    onClick={() => void onAddItem()}
                    disabled={busy}
                  >
                    Add plan item
                  </button>
                </div>
              </div>
              <div className="journey-form">
                <div className="journey-field">
                  <label className="journey-field__label" htmlFor="journey-outcome">
                    Visit outcome (records once at finalize)
                  </label>
                  <select
                    id="journey-outcome"
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    disabled={busy}
                  >
                    <option value="">Finalize without an outcome</option>
                    <option value="treatment_advised">Treatment advised</option>
                    <option value="treatment_accepted">Treatment accepted</option>
                    <option value="treatment_declined">Treatment declined</option>
                  </select>
                  <span className="journey-field__hint">
                    Outcome recording needs the appointment marked fulfilled
                    (step 3).
                  </span>
                </div>
                {outcome === 'treatment_declined' ? (
                  <div className="journey-field">
                    <label
                      className="journey-field__label"
                      htmlFor="journey-decline-reason"
                    >
                      Decline reason
                    </label>
                    <select
                      id="journey-decline-reason"
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value)}
                      disabled={busy}
                    >
                      <option value="">No reason recorded</option>
                      {DECLINE_REASON_OPTIONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div className="journey-actions">
                  <button
                    type="button"
                    className="ac-btn ac-btn--primary"
                    onClick={() => void onFinalize()}
                    disabled={busy}
                  >
                    {busy ? 'Finalizing...' : 'Finalize visit'}
                  </button>
                </div>
              </div>
            </>
          ) : null}
          <FeedbackLine feedback={feedback} />
        </>
      )}
    </StepCard>
  );
}

// --- Step 5: Payment -------------------------------------------------------------

export function PaymentStepCard({
  step,
  ctx,
  me,
  onChanged,
}: {
  step: JourneyStepState;
  ctx: JourneyLeadContext | null;
  me: DashboardMe;
  onChanged: () => void;
}): JSX.Element {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [category, setCategory] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());

  const allowed = canRecordPayment(me.role);
  const patientLinked = ctx !== null && ctx.conversion.patientId !== null;

  const onRecord = async (): Promise<void> => {
    if (ctx === null || ctx.conversion.patientId === null || ctx.lead.clinicId === null)
      return;
    const minorUnits = parseInrToMinorUnits(amount);
    if (minorUnits === null) {
      setAmountError('Enter a valid rupee amount, e.g. 1500 or 1500.50.');
      return;
    }
    setAmountError(null);
    setBusy(true);
    setFeedback(null);
    try {
      await recordPatientPaymentApi({
        clinicId: ctx.lead.clinicId,
        patientId: ctx.conversion.patientId,
        appointmentId: ctx.currentAppointmentId,
        amountMinorUnits: minorUnits,
        method: method === '' ? null : method,
        category: category === '' ? null : category,
        occurredAt: new Date().toISOString(),
        requestId,
      });
      setFeedback({ kind: 'success', text: 'Payment recorded.' });
      setAmount('');
      setRequestId(crypto.randomUUID());
      onChanged();
    } catch {
      setFeedback({
        kind: 'error',
        text: 'Could not record the payment. Check the amount and try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <StepCard step={step}>
      {ctx === null ? null : !patientLinked ? (
        <p className="journey-note">Link the patient first (step 2).</p>
      ) : (
        <>
          {ctx.payments !== null ? (
            <dl className="journey-facts">
              <div>
                <dt>Collected</dt>
                <dd>{formatMinorUnitsInr(ctx.payments.totalMinorUnits)}</dd>
              </div>
              <div>
                <dt>Payments</dt>
                <dd>{ctx.payments.count}</dd>
              </div>
              <div>
                <dt>Last payment</dt>
                <dd>
                  {ctx.payments.lastPaymentAt === null
                    ? 'None yet'
                    : new Date(ctx.payments.lastPaymentAt).toLocaleDateString('en-IN')}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="journey-note">
              Payment history is not available right now.
            </p>
          )}
          {!allowed ? (
            <p className="journey-note">
              The billing entry role records payments (receptionist by default;
              COC and owner supervise).
            </p>
          ) : (
            <>
              <div className="journey-form">
                <div className="journey-field">
                  <label className="journey-field__label" htmlFor="journey-amount">
                    Amount (INR)
                  </label>
                  <input
                    id="journey-amount"
                    className="journey-field__input"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="e.g. 1500"
                    disabled={busy}
                  />
                  {amountError ? (
                    <span className="journey-field__error">{amountError}</span>
                  ) : (
                    <span className="journey-field__hint">
                      Collected amount only - stored in exact paise.
                    </span>
                  )}
                </div>
                <div className="journey-field">
                  <label className="journey-field__label" htmlFor="journey-method">
                    Method
                  </label>
                  <select
                    id="journey-method"
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    disabled={busy}
                  >
                    <option value="">Not recorded</option>
                    {PAYMENT_METHOD_OPTIONS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="journey-field">
                  <label className="journey-field__label" htmlFor="journey-category">
                    Category
                  </label>
                  <select
                    id="journey-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    disabled={busy || ctx.categoryOptions.length === 0}
                  >
                    <option value="">Not recorded</option>
                    {ctx.categoryOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="journey-actions">
                  <button
                    type="button"
                    className="ac-btn ac-btn--primary"
                    onClick={() => void onRecord()}
                    disabled={busy}
                  >
                    {busy ? 'Recording...' : 'Record payment'}
                  </button>
                </div>
              </div>
              <p className="journey-note">
                Corrections are append-only: a wrong entry is voided by a
                supervisor in <Link to="/dashboard/billing-entry">Billing entry</Link>
                , then re-entered.
              </p>
            </>
          )}
          <FeedbackLine feedback={feedback} />
        </>
      )}
    </StepCard>
  );
}

// --- the page -------------------------------------------------------------------

export default function TestJourneyPage(): JSX.Element {
  const meCtx = useDashboardMeContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leadInput, setLeadInput] = useState(searchParams.get('leadId') ?? '');
  const [ctx, setCtx] = useState<JourneyLeadContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async (leadId: number): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchJourneyLeadContext(leadId);
      setCtx(data);
    } catch {
      setCtx(null);
      setLoadError(
        'Lead not found in your scope. Check the ID, or open it from the Lead inbox.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Deep-link support: /dashboard/test-journey?leadId=N loads immediately.
  useEffect(() => {
    const fromUrl = searchParams.get('leadId');
    if (fromUrl !== null && /^\d+$/.test(fromUrl)) {
      setLeadInput(fromUrl);
      void load(Number(fromUrl));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback((): void => {
    if (ctx !== null) void load(ctx.lead.leadId);
  }, [ctx, load]);

  const onOpen = (): void => {
    if (!/^\d+$/.test(leadInput.trim())) {
      setLoadError('Enter a numeric lead ID.');
      return;
    }
    const id = Number(leadInput.trim());
    setSearchParams({ leadId: String(id) }, { replace: true });
    void load(id);
  };

  const steps = useMemo(() => deriveJourneySteps(ctx), [ctx]);
  const me = meCtx?.me ?? null;

  return (
    <div className="clinic-table journey" data-testid="test-journey-page">
      <div className="coc-cockpit__head">
        <h2 className="clinic-table__title">
          <RouteIcon size={18} aria-hidden="true" /> Test clinic journey
        </h2>
        <span className="cockpit-pill">AdoraCare operating spine</span>
      </div>
      <p className="clinic-workbench__hint">
        Run one lead through AdoraCare&apos;s own spine: lead, patient, visit,
        diagnosis and treatment, collected payment. Every step uses the real
        product paths - nothing here is simulated.
      </p>

      <div className="journey-form">
        <div className="journey-field">
          <label className="journey-field__label" htmlFor="journey-lead-id">
            Lead ID
          </label>
          <input
            id="journey-lead-id"
            className="journey-field__input"
            inputMode="numeric"
            value={leadInput}
            onChange={(e) => setLeadInput(e.target.value)}
            placeholder="e.g. 42"
            disabled={loading}
          />
          {loadError ? (
            <span className="journey-field__error" role="alert">
              {loadError}
            </span>
          ) : (
            <span className="journey-field__hint">
              Pick a lead from the{' '}
              <Link to="/dashboard/lead-inbox">Lead inbox</Link> or enter its ID.
            </span>
          )}
        </div>
        <div className="journey-actions">
          <button
            type="button"
            className="ac-btn ac-btn--primary"
            onClick={onOpen}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Open journey'}
          </button>
        </div>
      </div>

      <StepRail steps={steps} />

      {ctx !== null && isJourneyComplete(ctx) ? (
        <div className="journey-complete" role="status">
          Journey complete: this lead became a patient, was seen, and paid.
        </div>
      ) : null}

      {me !== null ? (
        <div className="journey__cards">
          <LeadStepCard step={steps[0]!} ctx={ctx} />
          <PatientStepCard step={steps[1]!} ctx={ctx} me={me} onLinked={refresh} />
          <VisitStepCard step={steps[2]!} ctx={ctx} me={me} onChanged={refresh} />
          <TreatmentStepCard step={steps[3]!} ctx={ctx} me={me} onChanged={refresh} />
          <PaymentStepCard step={steps[4]!} ctx={ctx} me={me} onChanged={refresh} />
        </div>
      ) : null}
    </div>
  );
}
